import { useCallback, useState } from "react";
import { lprScoringServiceInstance as lprScoringService } from "../../services/LPRScoringService";

const PAGE_SIZE = 50;

/**
 * Owns the Camera Scoring review workflow state: fetching the queue,
 * loading/error state, the current read index, the scored/remaining counts, the
 * saved-score memory (so Previous shows a read's saved value), and the
 * Previous / Skip / Save-Next transitions.
 *
 * The component that uses this hook owns rendering only.
 */
const useLPRScoringQueue = () => {
  const [reads, setReads] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [scored, setScored] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [savedScores, setSavedScores] = useState({});

  /**
   * Load the queue for the given filters. Resets navigation to the first read.
   * @param {Object} filters - { deviceIDs, startDateTime, endDateTime }
   * @param {Object} cancelToken - optional axios cancel token
   */
  const loadQueue = useCallback(async (filters, cancelToken) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await lprScoringService.getQueue(
        filters,
        { limit: PAGE_SIZE, offset: 0 },
        cancelToken
      );
      const data = response?.data ?? {};
      setReads(data.collection ?? []);
      setRemaining(data.remaining ?? 0);
      setScored(data.scored ?? 0);
      setCurrentIndex(0);
      setSavedScores({});
      setHasLoaded(true);
    } catch (err) {
      setError(err);
      setReads([]);
      setRemaining(0);
      setScored(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /** Go to the previous read without saving. */
  const goPrevious = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);

  /**
   * Go to the next read without saving; does not change the tally.
   * Stays put when there is no next read (the last read stays on screen).
   */
  const skip = useCallback(() => {
    setCurrentIndex((prev) => (prev < reads.length - 1 ? prev + 1 : prev));
  }, [reads.length]);

  /**
   * Persist the current read's score, then advance (if there is a next read) and
   * decrement remaining / increment scored. Re-scoring a read already
   * saved this session does not double-count. When this is the last read, it is
   * saved but stays on screen (no next to advance to).
   * @param {Object} score - { incorrectCharacters, isException }
   */
  const saveAndNext = useCallback(
    async (score) => {
      const current = reads[currentIndex];
      if (!current) return;

      await lprScoringService.saveScore(current.lprId, score);

      const alreadyScored = Object.prototype.hasOwnProperty.call(savedScores, current.lprId);
      setSavedScores((prev) => ({ ...prev, [current.lprId]: score }));

      if (!alreadyScored) {
        setRemaining((prev) => Math.max(0, prev - 1));
        setScored((prev) => prev + 1);
      }

      setCurrentIndex((prev) => (prev < reads.length - 1 ? prev + 1 : prev));
    },
    [reads, currentIndex, savedScores]
  );

  const currentRead = reads[currentIndex] ?? null;
  const savedScoreForCurrent = currentRead
    ? savedScores[currentRead.lprId] ?? null
    : null;

  const hasNext = reads.length > 0 && currentIndex < reads.length - 1;
  const isCurrentScored = currentRead
    ? Object.prototype.hasOwnProperty.call(savedScores, currentRead.lprId)
    : false;

  return {
    reads,
    currentRead,
    currentIndex,
    savedScoreForCurrent,
    remaining,
    scored,
    isLoading,
    error,
    hasLoaded,
    hasNext,
    isCurrentScored,
    loadQueue,
    goPrevious,
    skip,
    saveAndNext,
  };
};

export default useLPRScoringQueue;
